import { useRef, useState } from 'react';

import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';

import { ConfirmationDialog, FormDialog } from '~components';
import { useSnackbar } from '~hooks';

import AccessRulesDataGrid from '../components/dataGrids/accessRulesDataGrid';
import SkcAccessRuleForm from '../forms/skcRuleForm/skcAccessRuleForm';
import { AccessRuleNew } from '../interfaces/accessRule';
import SkcAccessRulesService from '../services/skcAccessRulesService';

type UserAction =
	| {
			type: 'delete' | 'edit';
			label?: string;
			id: string;
	  }
	| {
			type: 'create';
			label?: never;
			id?: never;
	  };

const service = new SkcAccessRulesService();

const AccessRulesOverviewContainer = () => {
	const { t } = useTranslation();
	const { enqueueSuccessSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();
	const [userAction, setUserAction] = useState<UserAction | null>(null);
	const dataGridRef = useRef<{ mutate: () => void }>(null);

	const { trigger: triggerCreate, isMutating: isCreateMutating } = useSWRMutation(
		service.basePath,
		(_, { arg }: { arg: AccessRuleNew }) => service.createAccessRule(arg),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.successfullyAdded')} ${t('rule').toLowerCase()}`);
				setUserAction(null);
				dataGridRef.current?.mutate();
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { trigger: triggerEdit, isMutating: isEditMutating } = useSWRMutation(
		userAction?.id ? [service.basePath, userAction.id] : null,
		([_, id], { arg }: { arg: AccessRuleNew }) => service.updateAccessRule(id, arg),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.success.message.updated')} ${t('rule').toLowerCase()}`);
				setUserAction(null);
				dataGridRef.current?.mutate();
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { trigger: triggerDelete, isMutating: isDeleteMutating } = useSWRMutation(
		userAction?.id ? [service.basePath, userAction.id] : null,
		([_, id]) => service.deleteAccessRule(id),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.successfullyDeleted')} ${t('rule').toLowerCase()}`);
				setUserAction(null);
				dataGridRef.current?.mutate();
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	return (
		<Stack
			sx={{
				height: 1,
			}}
		>
			<AccessRulesDataGrid
				ref={dataGridRef}
				onEdit={(value) => setUserAction({ type: 'edit', ...value })}
				onDelete={(value) => setUserAction({ type: 'delete', ...value })}
				onCreate={() => setUserAction({ type: 'create' })}
			/>
			<FormDialog
				title={t(userAction?.type === 'edit' ? 'editResource' : 'addResource', {
					resource: t('rule'),
				})}
				open={userAction?.type === 'create' || userAction?.type === 'edit'}
				loading={isCreateMutating || isEditMutating}
				onClose={() => setUserAction(null)}
				maxWidth='md'
				fullWidth
			>
				<SkcAccessRuleForm
					id={userAction?.id}
					onSubmit={async (data) => {
						if (userAction?.type === 'create') {
							await triggerCreate(data);
						} else if (userAction?.type === 'edit') {
							await triggerEdit(data);
						} else {
							console.error('Useraction not supported', userAction);
						}
					}}
				/>
			</FormDialog>
			<ConfirmationDialog
				loading={isDeleteMutating}
				open={userAction?.type === 'delete'}
				subTitle={t('thisWillDeleteResourceWithName', {
					resource: t('rule'),
					name: userAction?.label ?? userAction?.id,
				})}
				onConfirm={async () => await triggerDelete()}
				onClose={() => setUserAction(null)}
			/>
		</Stack>
	);
};

export default AccessRulesOverviewContainer;
